import { useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useRegisterSW } from 'virtual:pwa-register/react';

import { Button } from '@/components/ui/Button';
import { Banner } from '@/components/ui/Surfaces';
import { cn } from '@/lib/cn';

/**
 * Instalacion y actualizacion de la PWA (seccion 16).
 * El service worker avisa de la version nueva en lugar de recargar por su
 * cuenta (RF-PUB-07): recargar sin avisar en mitad de un envio seria perder
 * el formulario del atleta.
 */

export function PwaPrompts() {
  return (
    <>
      <UpdatePrompt />
      <InstallPrompt />
    </>
  );
}

function UpdatePrompt() {
  const { t } = useTranslation();
  const {
    needRefresh: [needRefresh, setNeedRefresh],
    updateServiceWorker,
  } = useRegisterSW({
    onRegisterError(error) {
      console.error('[pwa] no se pudo registrar el service worker', error);
    },
  });

  if (!needRefresh) return null;

  return (
    <div className="fixed inset-x-4 bottom-24 z-50 mx-auto max-w-md md:bottom-6">
      <Banner
        tone="gold"
        action={
          <div className="flex gap-2">
            <Button size="sm" variant="ghost" onClick={() => setNeedRefresh(false)}>
              {t('common.close')}
            </Button>
            <Button size="sm" onClick={() => void updateServiceWorker(true)}>
              {t('pwa.updateAction')}
            </Button>
          </div>
        }
      >
        {t('pwa.updateAvailable')}
      </Banner>
    </div>
  );
}

type Platform = 'android' | 'ios' | 'desktop' | 'unsupported';

function detectPlatform(): Platform {
  if (typeof navigator === 'undefined') return 'unsupported';
  const ua = navigator.userAgent;
  if (/iPad|iPhone|iPod/.test(ua)) return 'ios';
  if (/Android/.test(ua)) return 'android';
  if (/Chrome|Edg/.test(ua)) return 'desktop';
  return 'unsupported';
}

/** true si ya se abre como aplicacion instalada. */
function isStandalone(): boolean {
  if (typeof window === 'undefined') return false;
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    // Safari en iOS usa esta propiedad propietaria.
    (window.navigator as Navigator & { standalone?: boolean }).standalone === true
  );
}

const DISMISS_KEY = 'cp.install.dismissed';

function InstallPrompt() {
  const { t } = useTranslation();
  const [deferred, setDeferred] = useState<BeforeInstallPromptEvent | null>(null);
  const [visible, setVisible] = useState(false);
  const platform = detectPlatform();

  useEffect(() => {
    if (isStandalone()) return;
    try {
      if (window.localStorage.getItem(DISMISS_KEY)) return;
    } catch {
      /* sin almacenamiento: se muestra igualmente */
    }

    const onBeforeInstall = (event: BeforeInstallPromptEvent) => {
      // Se guarda el evento para lanzar el dialogo cuando el usuario quiera,
      // no de golpe nada mas entrar.
      event.preventDefault();
      setDeferred(event);
      setVisible(true);
    };

    window.addEventListener('beforeinstallprompt', onBeforeInstall);

    // Safari en iOS no dispara el evento: se explican los pasos a mano
    // (RF-PUB-04).
    const iosTimer =
      platform === 'ios' ? window.setTimeout(() => setVisible(true), 4000) : undefined;

    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstall);
      if (iosTimer) window.clearTimeout(iosTimer);
    };
  }, [platform]);

  const dismiss = () => {
    setVisible(false);
    try {
      window.localStorage.setItem(DISMISS_KEY, '1');
    } catch {
      /* se volvera a ofrecer en la proxima visita */
    }
  };

  const install = async () => {
    if (!deferred) return;
    await deferred.prompt();
    await deferred.userChoice;
    setDeferred(null);
    dismiss();
  };

  if (!visible || platform === 'unsupported') return null;

  return (
    <div className="fixed inset-x-4 bottom-24 z-50 mx-auto max-w-md md:bottom-6">
      <div className="rounded-xl border border-line bg-surface p-4 shadow-card">
        <p className="font-display text-base uppercase tracking-epigraphic">
          {t('pwa.installTitle')}
        </p>
        <p className="mt-1 text-sm text-ink-muted">{t('pwa.installBody')}</p>

        <p className="mt-2 text-sm text-ink-muted">
          {platform === 'ios' && (
            <Trans
              i18nKey="pwa.installIos"
              components={{ 1: <strong className="text-ink" />, 3: <strong className="text-ink" /> }}
            />
          )}
          {platform === 'android' && !deferred && (
            <Trans
              i18nKey="pwa.installAndroid"
              components={{ 1: <strong className="text-ink" /> }}
            />
          )}
          {platform === 'desktop' && !deferred && (
            <Trans i18nKey="pwa.installDesktop">
              Pulsa el icono de instalación en la barra de direcciones.
            </Trans>
          )}
        </p>

        <div className="mt-3 flex justify-end gap-2">
          <Button size="sm" variant="ghost" onClick={dismiss}>
            {t('common.close')}
          </Button>
          {deferred && (
            <Button size="sm" onClick={() => void install()}>
              {t('pwa.installAction')}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

export function OfflineBanner({ className }: { className?: string }) {
  const { t } = useTranslation();
  return (
    <Banner tone="warning" className={cn(className)}>
      <span className="font-medium">{t('pwa.offlineTitle')}</span> — {t('pwa.offlineBody')}
    </Banner>
  );
}
